"use client"

import { useState, useEffect } from "react"
import { db } from "@/lib/firebase"
import { collection, getDocs, query, collectionGroup } from "firebase/firestore"
import { motion } from "framer-motion"
import { 
  Users, BookOpen, Database, MessageSquare, 
  FileText, Shield, Award, HelpCircle, 
  LayoutGrid, Activity, Clock, ArrowUpRight,
  TrendingUp, Eye, Zap, Cpu, Server, Wifi
} from "lucide-react"
import { Loader2 } from "lucide-react"

export default function AdminOverview() {
  const [stats, setStats] = useState<any>({
    users: 0,
    admins: 0,
    courses: 0,
    faqs: 0,
    reviews: 0,
    quizzes: 0,
    testSeries: 0,
    articles: 0,
    magazines: 0,
    staff: 0,
    testimonials: 0
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchAllStats = async () => {
      try {

        const fetchedStats: any = { ...stats }
        const counts = [
          { key: 'users', path: 'users', type: 'collection' },
          { key: 'admins', path: 'admins', type: 'collection' },
          { key: 'courses', path: 'courses', type: 'collection' },
          { key: 'faqs', path: 'faqs', type: 'group' },
          { key: 'reviews', path: 'reviews', type: 'group' },
          { key: 'quizzes', path: 'quizzes', type: 'collection' },
          { key: 'testSeries', path: 'test_series', type: 'collection' },
          { key: 'articles', path: 'articles', type: 'collection' },
          { key: 'magazines', path: 'magazines', type: 'collection' },
          { key: 'staff', path: 'staff', type: 'collection' },
          { key: 'testimonials', path: 'testimonials', type: 'collection' }
        ]

        await Promise.all(counts.map(async (item) => {
          try {
            console.log(`Fetching stats for: ${item.key} (${item.path})`)
            const ref = item.type === 'group' 
              ? query(collectionGroup(db, item.path))
              : collection(db, item.path)
            
            const snap = await getDocs(ref)
            fetchedStats[item.key] = snap.size || 0
          } catch (e) {
            console.warn(`Could not fetch count for ${item.path}:`, e)
            fetchedStats[item.key] = 0
          }
        }))
        
        console.log("Stats update complete:", fetchedStats)
        setStats(fetchedStats)
      } catch (error) {
        console.error("Error fetching admin stats:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchAllStats()
  }, [])

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#b48c36]" size={40} />
      </div>
    )
  }

  const mainStats = [
    { label: "Total Platform Users", value: stats.users + stats.admins, detail: "Active Registrations", icon: Users },
    { label: "Active Courses", value: stats.courses, detail: "Learning Modules", icon: Shield },
    { label: "Digital Materials", value: stats.articles + stats.magazines, detail: "Content Library", icon: FileText },
    { label: "Database Health", value: "Active", detail: "System Ready", icon: Activity },
  ]

  return (
    <div className="space-y-6 sm:space-y-8 animate-in fade-in duration-700">
      
      {/* 1. Cinematic Hero Header - Responsive */}
      <div className="relative bg-slate-950 rounded-lg overflow-hidden border border-white/10 shadow-2xl min-h-fit sm:min-h-[250px] flex items-center text-left">
         {/* Abstract SVG Background Pattern */}
         <div className="absolute inset-0 opacity-20 pointer-events-none">
            <svg width="100%" height="100%" viewBox="0 0 800 400" xmlns="http://www.w3.org/2000/svg">
               <defs>
                  <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
                     <stop offset="0%" style={{ stopColor: '#b48c36', stopOpacity: 0.1 }} />
                     <stop offset="100%" style={{ stopColor: '#000', stopOpacity: 0.5 }} />
                  </linearGradient>
               </defs>
               <path d="M0,100 C150,200 350,0 500,100 C650,200 800,0 800,100 L800,400 L0,400 Z" fill="url(#grad1)" />
            </svg>
         </div>

         <div className="relative z-10 p-6 sm:p-10 w-full flex flex-col sm:flex-row items-center justify-between gap-6 sm:gap-12 text-center sm:text-left">
            <div className="max-w-xl">
               <div className="flex items-center gap-3 mb-3 sm:mb-6 bg-white/5 w-fit px-3 py-1 rounded-full border border-white/10 mx-auto sm:mx-0">
                  <motion.div 
                    animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                    className="w-1 h-1 rounded-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]" 
                  />
                  <span className="text-[8px] sm:text-[10px] font-bold uppercase tracking-widest text-slate-300">Database Management</span>
               </div>
               <h1 className="text-2xl sm:text-4xl lg:text-5xl font-serif font-bold text-white mb-2 sm:mb-6 leading-tight">
                  Database <br className="hidden sm:block"/> <span className="text-[#b48c36]">Overview.</span>
               </h1>
            </div>

            {/* Futuristic Animated Aesthetic HUD - Scaled for Mobile */}
            <div className="relative flex items-center justify-center h-24 w-24 sm:h-32 sm:w-32 lg:h-48 lg:w-48 group">
               {/* White Orbital Rings */}
               <motion.div 
                 animate={{ rotate: 360 }}
                 transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                 className="absolute inset-0 border border-white/10 rounded-full"
               />
               <motion.div 
                 animate={{ rotate: -360 }}
                 transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
                 className="absolute inset-2 sm:inset-4 border border-white/5 rounded-full border-dashed"
               />
               
               {/* Aesthetic Center */}
               <div className="relative flex flex-col items-center">
                  <div className="relative">
                     <motion.div 
                        animate={{ scale: [1, 1.3, 1], opacity: [0.1, 0.3, 0.1] }}
                        transition={{ repeat: Infinity, duration: 4 }}
                        className="absolute inset-0 bg-white rounded-full blur-xl sm:blur-2xl"
                     />
                     <div className="relative p-3 sm:p-4 lg:p-6 bg-slate-900/50 backdrop-blur-sm border border-white/10 rounded-full shadow-2xl">
                        <Database size={16} className="text-white opacity-40 sm:hidden" />
                        <Database size={24} className="text-white opacity-40 hidden sm:block lg:hidden" />
                        <Database size={32} className="text-white opacity-40 hidden lg:block" />
                     </div>
                  </div>
               </div>

               {/* Corner Accents */}
               <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-white/20" />
               <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-white/20" />
               <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-white/20" />
               <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-white/20" />
            </div>
         </div>
      </div>

      {/* 2. Bespoke Stats Matrix */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
         {mainStats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-white p-4 sm:p-6 border border-slate-100 shadow-sm hover:shadow-2xl transition-all duration-700 relative overflow-hidden group"
            >
               {/* Decorative corner icon */}
               <div className="absolute top-0 right-0 p-2 opacity-[0.03] scale-125 sm:scale-150 rotate-12 group-hover:rotate-45 transition-transform">
                  <stat.icon size={80} />
               </div>

               <div className="relative z-10">
                  <p className="text-[8px] sm:text-[9px] font-bold uppercase tracking-[0.4em] text-slate-300 mb-2 sm:mb-4">{stat.label}</p>
                  <div className="flex items-center justify-between">
                     <h3 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-slate-950">{stat.value}</h3>
                     <div className="p-2 sm:p-3 bg-[#fcfaf7] border border-slate-50 text-[#b48c36] group-hover:bg-[#b48c36] group-hover:text-white transition-colors duration-500">
                        <stat.icon size={16} />
                     </div>
                  </div>
                  <div className="mt-2 sm:mt-4 flex items-center gap-2">
                     <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-[#b48c36]" />
                     <p className="text-[8px] sm:text-[10px] font-bold uppercase tracking-widest text-slate-500">{stat.detail}</p>
                  </div>
               </div>
            </motion.div>
         ))}
      </div>

      {/* 3. Detailed Data Table (Bespoke Grid) */}
      <div className="grid grid-cols-1 gap-8">
         <div className="bg-white border border-slate-100 p-6 sm:p-8 shadow-sm">
            <div className="flex items-center justify-between mb-6 sm:mb-10">
               <div>
                  <h4 className="text-lg sm:text-xl font-serif font-bold text-slate-900">Registry Distribution</h4>
                  <p className="text-[10px] sm:text-xs text-slate-400 font-medium">Categorical breakdown of assets.</p>
               </div>
               <LayoutGrid size={18} className="text-slate-200" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-6 sm:gap-y-10 gap-x-6 sm:gap-x-12">
               {[
                 { label: "Quizzes", val: stats.quizzes, icon: HelpCircle },
                 { label: "Test Series", val: stats.testSeries, icon: Database },
                 { label: "Staff", val: stats.staff, icon: Award },
                 { label: "Reviews", val: stats.reviews, icon: MessageSquare },
                 { label: "Testimonials", val: stats.testimonials, icon: TrendingUp },
                 { label: "FAQs", val: stats.faqs, icon: Eye },
               ].map((item) => (
                 <div key={item.label} className="group">
                    <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                       <item.icon size={12} className="text-[#b48c36]/40 group-hover:text-[#b48c36] transition-colors" />
                       <span className="text-[7px] sm:text-[9px] font-bold uppercase tracking-[0.2em] text-slate-400">{item.label}</span>
                    </div>
                    <div className="flex items-baseline justify-between border-b border-slate-50 pb-1 sm:pb-2">
                       <p className="text-xl sm:text-2xl font-serif font-medium text-slate-800">{item.val}</p>
                       <span className="text-[7px] sm:text-[9px] font-bold text-emerald-500">Live</span>
                    </div>
                 </div>
               ))}
            </div>
         </div>
      </div>

    </div>
  )
}
